/*
 * Venenon — product page behaviour: variant picker, media gallery, sticky buy bar.
 *
 * All product data comes from a JSON payload rendered by Liquid from Shopify's
 * `product` object (including money strings produced by Shopify money filters).
 * No IDs, prices, option values or model names are written in this file.
 */

(() => {
  const ready = (fn) =>
    document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', fn) : fn();

  /**
   * Horizontal-only equivalent of `scrollIntoView({ block: 'nearest' })`.
   * The native call also scrolls vertically scrollable ancestors, so marking
   * the first thumbnail active during initialisation would pull the whole page
   * down to the gallery on load.
   * @param {Element} node
   */
  function railIntoView(node) {
    let rail = node && node.parentElement;
    while (rail && rail !== document.body && rail !== document.documentElement) {
      const overflow = getComputedStyle(rail).overflowX;
      if ((overflow === 'auto' || overflow === 'scroll') && rail.scrollWidth > rail.clientWidth + 1) break;
      rail = rail.parentElement;
    }
    if (!rail || rail === document.body || rail === document.documentElement) return;

    const railBox = rail.getBoundingClientRect();
    const box = node.getBoundingClientRect();
    if (box.left < railBox.left) rail.scrollLeft += box.left - railBox.left;
    else if (box.right > railBox.right) rail.scrollLeft += box.right - railBox.right;
  }

  function readPayload(root) {
    const script = root.querySelector('[data-ven-product-json]');
    if (!script) return null;
    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      console.warn('[venenon] invalid product payload', error);
      return null;
    }
  }

  /* ------------------------------------------------------- Variant picker */

  function initVariantPicker(root) {
    const picker = root.querySelector('[data-ven-variant-picker]');
    const data = readPayload(root);
    if (!picker || !data) return;

    const idInput = root.querySelector('[data-ven-variant-id]');
    const inputs = [...picker.querySelectorAll('input[type="radio"], select')];

    function selectedOptions() {
      const values = [];
      picker.querySelectorAll('[data-ven-option-group]').forEach((group) => {
        const index = Number(group.dataset.venOptionGroup);
        const select = group.querySelector('select');
        if (select) {
          values[index] = select.value;
          return;
        }
        const checked = group.querySelector('input[type="radio"]:checked');
        values[index] = checked ? checked.value : null;
      });
      return values;
    }

    function findVariant(values) {
      return (
        data.variants.find((variant) => variant.options.every((option, i) => option === values[i])) || null
      );
    }

    function setText(selector, value) {
      root.querySelectorAll(selector).forEach((el) => {
        el.innerHTML = value ?? '';
      });
    }

    function toggle(selector, show) {
      root.querySelectorAll(selector).forEach((el) => {
        el.hidden = !show;
      });
    }

    function markAvailability(values) {
      picker.querySelectorAll('[data-ven-option-group]').forEach((group) => {
        const index = Number(group.dataset.venOptionGroup);
        group.querySelectorAll('[data-ven-option-value]').forEach((node) => {
          const candidate = values.slice();
          candidate[index] = node.dataset.venOptionValue;
          const match = data.variants.find((variant) =>
            variant.options.every((option, i) => (candidate[i] == null ? true : option === candidate[i]))
          );
          const available = Boolean(match && match.available);
          const label = node.closest('label') || node;
          label.classList.toggle('ven-option--unavailable', !available);
          // Not colour-only: unavailable values are also line-struck in CSS and
          // announced through the hidden availability text.
          const note = label.querySelector('[data-ven-option-availability]');
          if (note) note.textContent = available ? '' : node.dataset.venUnavailableText || '';
        });
      });
    }

    function update({ initial = false } = {}) {
      const values = selectedOptions();
      const variant = findVariant(values);
      markAvailability(values);

      root.querySelectorAll('[data-ven-selected-option]').forEach((el) => {
        const index = Number(el.dataset.venSelectedOption);
        el.textContent = values[index] ?? '';
      });

      if (idInput) idInput.value = variant ? String(variant.id) : '';

      const addButtons = root.querySelectorAll('[data-ven-add-to-cart]');
      const buyNow = root.querySelectorAll('[data-ven-dynamic-checkout]');

      if (!variant) {
        setText('[data-ven-price]', data.labels.unavailable);
        toggle('[data-ven-compare-price]', false);
        toggle('[data-ven-sale-badge]', false);
        addButtons.forEach((button) => {
          button.disabled = true;
          button.dataset.soldOut = 'true';
          const label = button.querySelector('[data-ven-add-label]');
          if (label) label.textContent = data.labels.unavailable;
        });
        buyNow.forEach((el) => (el.hidden = true));
      } else {
        setText('[data-ven-price]', variant.price);
        const onSale = Boolean(variant.compare_at_price);
        setText('[data-ven-compare-price]', variant.compare_at_price || '');
        toggle('[data-ven-compare-price]', onSale);
        setText('[data-ven-discount-badge]', variant.discount_label || '');
        toggle('[data-ven-sale-badge]', onSale);
        setText('[data-ven-unit-price]', variant.unit_price || '');
        toggle('[data-ven-unit-price]', Boolean(variant.unit_price));
        setText('[data-ven-sku]', variant.sku || '');
        toggle('[data-ven-sku-wrapper]', Boolean(variant.sku));
        toggle('[data-ven-inventory]', Boolean(variant.inventory_label));
        setText('[data-ven-inventory]', variant.inventory_label || '');

        addButtons.forEach((button) => {
          button.disabled = !variant.available;
          button.dataset.soldOut = variant.available ? 'false' : 'true';
          const label = button.querySelector('[data-ven-add-label]');
          if (label) label.textContent = variant.available ? data.labels.add_to_cart : data.labels.sold_out;
        });
        buyNow.forEach((el) => (el.hidden = !variant.available));

        // Quantity rules from the variant, when the shop defines them.
        const quantityInput = root.querySelector('[data-ven-quantity-input]');
        if (quantityInput && variant.quantity_rule) {
          quantityInput.min = variant.quantity_rule.min;
          quantityInput.step = variant.quantity_rule.increment;
          if (variant.quantity_rule.max) quantityInput.max = variant.quantity_rule.max;
          else quantityInput.removeAttribute('max');
          if (Number(quantityInput.value) < Number(quantityInput.min)) quantityInput.value = quantityInput.min;
        }

        if (!initial) {
          // Keep the address bar in sync without a reload.
          const url = new URL(window.location.href);
          url.searchParams.set('variant', String(variant.id));
          window.history.replaceState({}, '', url);
        }

        if (variant.featured_media_id != null) {
          root.dispatchEvent(
            new CustomEvent('venenon:media:select', {
              detail: { mediaId: String(variant.featured_media_id) },
              bubbles: true,
            })
          );
        }
      }

      document.dispatchEvent(
        new CustomEvent('venenon:variant:change', { detail: { variant, sectionId: root.dataset.sectionId } })
      );
    }

    inputs.forEach((input) => input.addEventListener('change', () => update()));
    update({ initial: true });
  }

  /* -------------------------------------------------------------- Gallery */

  function initGallery(root) {
    const gallery = root.querySelector('[data-ven-gallery]');
    if (!gallery) return;

    const viewport = gallery.querySelector('[data-ven-gallery-viewport]');
    const slides = [...gallery.querySelectorAll('[data-ven-gallery-slide]')];
    const thumbs = [...gallery.querySelectorAll('[data-ven-gallery-thumb]')];
    const prev = gallery.querySelector('[data-ven-gallery-prev]');
    const next = gallery.querySelector('[data-ven-gallery-next]');
    const counter = gallery.querySelector('[data-ven-gallery-counter]');
    if (!slides.length) return;

    let index = 0;

    function show(newIndex, { focusThumb = false } = {}) {
      index = (newIndex + slides.length) % slides.length;
      slides.forEach((slide, i) => {
        const active = i === index;
        slide.classList.toggle('ven-is-active', active);
        slide.setAttribute('aria-hidden', active ? 'false' : 'true');
        const media = slide.querySelector('video');
        if (media && !active) media.pause?.();
        // Defer loading of non-initial media until it is first shown.
        if (active) {
          slide.querySelectorAll('img[data-ven-src]').forEach((img) => {
            img.src = img.dataset.venSrc;
            if (img.dataset.venSrcset) img.srcset = img.dataset.venSrcset;
            img.removeAttribute('data-ven-src');
          });
        }
      });
      thumbs.forEach((thumb, i) => {
        const active = i === index;
        thumb.setAttribute('aria-current', active ? 'true' : 'false');
        thumb.tabIndex = active ? 0 : -1;
        if (active && focusThumb) thumb.focus();
        if (active) railIntoView(thumb);
      });
      if (counter) counter.textContent = `${index + 1} / ${slides.length}`;
    }

    prev?.addEventListener('click', () => show(index - 1));
    next?.addEventListener('click', () => show(index + 1));

    thumbs.forEach((thumb, i) => {
      thumb.addEventListener('click', () => show(i));
      thumb.addEventListener('keydown', (event) => {
        const rtl = document.documentElement.dir === 'rtl';
        const fwd = rtl ? 'ArrowLeft' : 'ArrowRight';
        const back = rtl ? 'ArrowRight' : 'ArrowLeft';
        if (event.key === fwd) {
          event.preventDefault();
          show(i + 1, { focusThumb: true });
        } else if (event.key === back) {
          event.preventDefault();
          show(i - 1, { focusThumb: true });
        }
      });
    });

    // Touch swipe on the viewport.
    let startX = null;
    viewport?.addEventListener(
      'touchstart',
      (event) => {
        startX = event.touches[0].clientX;
      },
      { passive: true }
    );
    viewport?.addEventListener(
      'touchend',
      (event) => {
        if (startX == null) return;
        const delta = event.changedTouches[0].clientX - startX;
        if (Math.abs(delta) > 40) show(index + (delta < 0 ? 1 : -1));
        startX = null;
      },
      { passive: true }
    );

    viewport?.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowRight') show(index + 1);
      else if (event.key === 'ArrowLeft') show(index - 1);
    });

    root.addEventListener('venenon:media:select', (event) => {
      const target = slides.findIndex((slide) => slide.dataset.mediaId === event.detail.mediaId);
      if (target >= 0) show(target);
    });

    show(0);
  }

  /* ----------------------------------------------------- Sticky buy bar */

  function initStickyBar(root) {
    const bar = document.querySelector('[data-ven-sticky-bar]');
    if (!bar) return;
    const anchor = root.querySelector('[data-ven-sticky-anchor]');
    if (!anchor) return;

    let pastForm = false;
    let overlayOpen = false;

    function apply() {
      const show = pastForm && !overlayOpen;
      bar.dataset.visible = show ? 'true' : 'false';
      bar.setAttribute('aria-hidden', show ? 'false' : 'true');
      bar.querySelectorAll('button, a, input').forEach((el) => {
        if (show) el.removeAttribute('tabindex');
        else el.setAttribute('tabindex', '-1');
      });
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        pastForm = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        apply();
      },
      { threshold: 0 }
    );
    observer.observe(anchor);

    document.addEventListener('venenon:overlay', (event) => {
      overlayOpen = event.detail.open;
      apply();
    });

    // Horizon's cart drawer is a <dialog>; hide the bar while it is open.
    const cartDialog = document.querySelector('theme-drawer#cart-drawer dialog');
    if (cartDialog) {
      const mo = new MutationObserver(() => {
        overlayOpen = cartDialog.open;
        apply();
      });
      mo.observe(cartDialog, { attributes: true, attributeFilter: ['open'] });
    }

    // Mirror the live variant state into the bar.
    document.addEventListener('venenon:variant:change', (event) => {
      const { variant } = event.detail;
      bar.querySelectorAll('[data-ven-sticky-price]').forEach((el) => {
        el.innerHTML = variant ? variant.price : '';
      });
      bar.querySelectorAll('[data-ven-sticky-compare]').forEach((el) => {
        el.innerHTML = variant?.compare_at_price || '';
        el.hidden = !variant?.compare_at_price;
      });
      bar.querySelectorAll('[data-ven-sticky-option]').forEach((el) => {
        el.textContent = variant ? variant.title : '';
      });
      bar.querySelectorAll('[data-ven-sticky-image]').forEach((el) => {
        if (variant?.image) {
          el.src = variant.image;
          el.hidden = false;
        }
      });
      bar.querySelectorAll('[data-ven-sticky-submit]').forEach((button) => {
        button.disabled = !variant || !variant.available;
      });
    });

    apply();
  }

  /* -------------------------------------------------- Quantity stepper */

  function initQuantity(root) {
    root.querySelectorAll('[data-ven-quantity]').forEach((wrapper) => {
      const input = wrapper.querySelector('input');
      if (!input) return;
      wrapper.querySelectorAll('[data-ven-quantity-step]').forEach((button) => {
        button.addEventListener('click', () => {
          const step = Number(input.step) || 1;
          const delta = Number(button.dataset.venQuantityStep) * step;
          const min = Number(input.min) || 1;
          const max = input.max ? Number(input.max) : Infinity;
          input.value = String(Math.min(max, Math.max(min, (Number(input.value) || min) + delta)));
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
    });
  }

  /* ------------------------------------------------------------- Bundles */

  function initBundles(root) {
    // Bundle options are real variants or real bundle products supplied by Liquid.
    // The radio simply swaps which merchandise/quantity the form submits; the
    // price shown is whatever Shopify returned for that merchandise.
    root.querySelectorAll('[data-ven-bundle]').forEach((group) => {
      const idInput = root.querySelector('[data-ven-variant-id]');
      const quantityInput = root.querySelector('[data-ven-quantity-input]');
      group.querySelectorAll('input[type="radio"]').forEach((radio) => {
        radio.addEventListener('change', () => {
          if (radio.dataset.venBundleVariantId && idInput) idInput.value = radio.dataset.venBundleVariantId;
          if (radio.dataset.venBundleQuantity && quantityInput) {
            quantityInput.value = radio.dataset.venBundleQuantity;
            quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
      });
    });
  }

  function initSection(root) {
    if (root.__venProductInit) return;
    root.__venProductInit = true;
    initVariantPicker(root);
    initGallery(root);
    initQuantity(root);
    initBundles(root);
    initStickyBar(root);
  }

  function boot(scope = document) {
    scope.querySelectorAll('[data-ven-product-root]').forEach(initSection);
  }

  ready(() => boot());
  document.addEventListener('shopify:section:load', (event) => boot(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-ven-product-root]').forEach((el) => {
      delete el.__venProductInit;
    });
  });
})();
